import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";

function AuthShell({ children, title, subtitle, footer }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
           style={{ background: "linear-gradient(135deg, #1F3A8A 0%, #15265b 100%)" }}>
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #8B5CF6 0%, transparent 45%), radial-gradient(circle at 80% 80%, #3B82F6 0%, transparent 40%)" }}/>
        <div className="relative">
          <Logo variant="light" />
        </div>
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#FBBF24] mb-4">Bem-vindo, professor(a)</div>
          <h2 className="font-heading text-4xl font-extrabold text-white leading-tight">Do planejamento à avaliação em minutos.</h2>
          <p className="mt-4 text-white/70 max-w-md">Comece com 5 gerações gratuitas. Sem cartão.</p>
        </div>
        <div className="relative text-white/50 text-sm">© {new Date().getFullYear()} AulaPronta</div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">{title}</h1>
          <p className="mt-2 text-slate-600">{subtitle}</p>
          <div className="mt-8">{children}</div>
          <div className="mt-6 text-sm text-slate-600">{footer}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Olá, ${u.name}!`);
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Entrar" subtitle="Acesse sua conta AulaPronta."
      footer={<>Não tem conta? <Link to="/register" className="font-semibold text-[#8B5CF6]" data-testid="link-register">Criar conta grátis</Link></>}>
      <GoogleButton />
      <div className="flex items-center gap-3 my-5 text-xs uppercase tracking-widest text-slate-400 font-semibold">
        <div className="flex-1 h-px bg-slate-200" /> ou <div className="flex-1 h-px bg-slate-200" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-mail</Label>
          <Input type="email" required value={email} onChange={e => setEmail(e.target.value)}
            className="rounded-xl h-12" placeholder="voce@escola.com" data-testid="input-email"/>
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Senha</Label>
          <Input type="password" required value={password} onChange={e => setPassword(e.target.value)}
            className="rounded-xl h-12" placeholder="Sua senha" data-testid="input-password"/>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-login">
          {loading && <Loader2 className="animate-spin mr-2" size={16}/>} Entrar
        </Button>
        <div className="text-center pt-1">
          <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-[#8B5CF6]" data-testid="link-forgot">
            Esqueci minha senha
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const [step, setStep] = useState("form"); // "form" | "code"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/signup/request-code", { name, email, password });
      setStep("code");
      setCooldown(60);
      toast.success("Enviamos um código de 6 dígitos para seu e-mail.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    setLoading(true);
    try {
      await api.post("/auth/signup/resend-code", { email });
      setCooldown(60);
      toast.success("Novo código enviado.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup/verify-code", { email, code });
      localStorage.setItem("ap_token", data.token);
      try {
        const { trackCompleteRegistration } = await import("@/lib/pixel");
        trackCompleteRegistration({ method: "email" });
      } catch { /* pixel opcional */ }
      toast.success(`Bem-vindo(a), ${data.user.name}!`);
      window.location.href = "/dashboard";
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title={step === "form" ? "Criar conta grátis" : "Confirme seu e-mail"}
      subtitle={step === "form" ? "5 gerações gratuitas. Sem cartão."
        : `Enviamos um código de 6 dígitos para ${email}.`}
      footer={<>Já tem conta? <Link to="/login" className="font-semibold text-[#8B5CF6]" data-testid="link-login">Entrar</Link></>}>
      {step === "form" ? (
        <>
          <GoogleButton />
          <div className="flex items-center gap-3 my-5 text-xs uppercase tracking-widest text-slate-400 font-semibold">
            <div className="flex-1 h-px bg-slate-200" /> ou com e-mail <div className="flex-1 h-px bg-slate-200" />
          </div>
          <form onSubmit={requestCode} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nome</Label>
              <Input required value={name} onChange={e => setName(e.target.value)}
                className="rounded-xl h-12" placeholder="Como podemos te chamar?" data-testid="input-name"/>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-mail</Label>
              <Input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="rounded-xl h-12" placeholder="voce@escola.com" data-testid="input-email"/>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Senha</Label>
              <Input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
                className="rounded-xl h-12" placeholder="Mínimo 6 caracteres" data-testid="input-password"/>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full rounded-full h-12 bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-register">
              {loading && <Loader2 className="animate-spin mr-2" size={16}/>} Enviar código de confirmação
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Enviaremos um código de 6 dígitos para seu e-mail. Sua conta será criada após a confirmação.
            </p>
          </form>
        </>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Código de 6 dígitos</Label>
            <Input value={code} onChange={e => setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              inputMode="numeric" autoFocus
              className="rounded-xl h-14 text-center font-mono text-2xl tracking-[0.5em]"
              placeholder="000000" data-testid="input-signup-code"/>
            <p className="text-xs text-slate-500 mt-2">O código é válido por 5 minutos. Verifique também sua pasta de spam.</p>
          </div>
          <Button type="submit" disabled={loading || code.length !== 6}
            className="w-full rounded-full h-12 bg-[#1F3A8A] hover:bg-[#15265b]" data-testid="btn-verify-signup">
            {loading && <Loader2 className="animate-spin mr-2" size={16}/>} Confirmar e criar conta
          </Button>
          <div className="flex items-center justify-between text-sm">
            <button type="button" onClick={() => setStep("form")}
              className="text-slate-500 hover:text-slate-800" data-testid="btn-change-email">
              ← Alterar dados
            </button>
            <button type="button" onClick={resend} disabled={cooldown > 0 || loading}
              className="font-semibold text-[#8B5CF6] disabled:opacity-40" data-testid="btn-resend-code">
              {cooldown > 0 ? `Reenviar em ${cooldown}s` : "Reenviar código"}
            </button>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
